import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy as CopyIcon, RefreshCcw as RefreshCcwIcon, ChevronDown, Loader2 } from 'lucide-react';
import { Actions, Action } from 'shared/ui/shadcn-io/ai/actions';
import { Response } from 'shared/ui/shadcn-io/ai/response';
import { cn } from 'shared/utils';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from 'shared/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'shared/ui/dropdown-menu';
import {
  DEFAULT_CHAT_PANEL_STYLES,
  DEFAULT_CHAT_THEME,
  isLightLikeChatTheme,
} from 'features/chat/constants/panelStyles';
import {
  DEFAULT_ASSISTANT_LOADING_MESSAGES,
  DEFAULT_ASSISTANT_LOADING_MESSAGE_INTERVAL_MS,
} from 'features/chat/constants/loadingMessages';

const USER_ROLE = 'user';
const SYSTEM_ROLE = 'system';
const LOADING_MESSAGES = DEFAULT_ASSISTANT_LOADING_MESSAGES;
const LOADING_MESSAGE_INTERVAL_MS = DEFAULT_ASSISTANT_LOADING_MESSAGE_INTERVAL_MS;
const LOADING_ELLIPSIS_SEQUENCE = Object.freeze(['.', '..', '...']);
const LOADING_ELLIPSIS_INTERVAL_MS = 500;

const getMessageKey = (message, fallback) => {
  const rawId = message?.id;
  if (typeof rawId === 'string') {
    return rawId.trim() ? rawId : fallback;
  }
  if (typeof rawId === 'number') {
    return String(rawId);
  }
  return fallback;
};

const renderAttachments = (attachments = [], theme, panelStyles) => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return null;
  }

  const backgroundColor = theme === 'dark'
    ? 'rgba(37, 38, 48, 0.75)'
    : 'rgba(241, 245, 249, 0.85)';

  return (
    <div className="mt-3 flex flex-wrap gap-3">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="relative h-24 w-32 overflow-hidden rounded-lg border"
          style={{
            borderColor: panelStyles.borderColor,
            backgroundColor,
          }}
        >
          {attachment.dataUrl ? (
            <img
              src={attachment.dataUrl}
              alt={attachment.label || '첨부 이미지'}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};



const renderReasoning = (reasoning, status) => {
  if (!reasoning) {
    return null;
  }
  const summaries = Array.isArray(reasoning.summary) ? reasoning.summary.filter(Boolean) : [];
  const details = Array.isArray(reasoning.details) ? reasoning.details.filter(Boolean) : [];

  if (!summaries.length && !details.length) {
    return null;
  }

  return (
    <Reasoning className="mt-3" isStreaming={status === 'typing'}>
      <ReasoningTrigger />
      <ReasoningContent>
        {summaries.map((text, index) => (
          <p
            key={`reasoning-summary-${index}`}
            className="whitespace-pre-wrap text-sm leading-6 text-foreground/80"
          >
            {text}
          </p>
        ))}
        {details.length ? (
          <ChainOfThought className="mt-3" defaultOpen={false}>
            <ChainOfThoughtHeader>Chain of Thought</ChainOfThoughtHeader>
            <ChainOfThoughtContent>
              {details.map((detail, index) => (
                <ChainOfThoughtStep
                  key={`reasoning-step-${index}`}
                  status="complete"
                  label={`Step ${index + 1}`}
                >
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {detail}
                  </p>
                </ChainOfThoughtStep>
              ))}
            </ChainOfThoughtContent>
          </ChainOfThought>
        ) : null}
        {typeof reasoning.tokens === 'number' ? (
          <p className="mt-2 text-[11px] text-muted-foreground/80">
            Reasoning tokens · {reasoning.tokens}
          </p>
        ) : null}
      </ReasoningContent>
    </Reasoning>
  );
};


export default function ChatMessageList({
  title = 'Assistant',
  messages = [],
  endRef,
  onRetry,
  onCopy,
  onRetryWithModel,
  availableModels = [],
  theme = DEFAULT_CHAT_THEME,
  panelStyles = DEFAULT_CHAT_PANEL_STYLES,
  className,
  onContainerRef,
  userBubbleMaxWidth = 320,
  assistantMessageMaxWidth = 800,
  retryingMessageMap,
  isScrollable = true,
  isFullscreen = false,
}) {
  const [internalCopiedMap, setInternalCopiedMap] = useState({});
  const [internalSpinningMap, setInternalSpinningMap] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const scrollContainerRef = useRef(null);
  const [pendingMessageIndex, setPendingMessageIndex] = useState(0);
  const [ellipsisIndex, setEllipsisIndex] = useState(0);

  const resolvedPanelStyles = useMemo(
    () => ({ ...DEFAULT_CHAT_PANEL_STYLES, ...(panelStyles || {}) }),
    [panelStyles],
  );
  const resolvedTheme = theme || DEFAULT_CHAT_THEME;
  const isLightTheme = isLightLikeChatTheme(resolvedTheme);
  const isTyping = useMemo(
    () => messages.some((message) => message.status === 'typing' || message.status === 'pending'),
    [messages],
  );
  const hasPendingAssistantMessage = useMemo(
    () =>
      messages.some(
        (message) =>
          (message?.role || 'assistant') === 'assistant' &&
          (message.status === 'pending' || (message.status === 'typing' && !message.text)),
      ),
    [messages],
  );
  const pendingIndicatorText = useMemo(() => {
    if (!hasPendingAssistantMessage || !LOADING_MESSAGES.length) {
      return '';
    }
    const safeIndex = pendingMessageIndex % LOADING_MESSAGES.length;
    return LOADING_MESSAGES[safeIndex] || LOADING_MESSAGES[0];
  }, [hasPendingAssistantMessage, pendingMessageIndex]);
  const pendingIndicatorBaseText = useMemo(() => {
    if (!pendingIndicatorText) {
      return '';
    }
    if (pendingIndicatorText.endsWith('...')) {
      return pendingIndicatorText.slice(0, -3).trimEnd();
    }
    return pendingIndicatorText;
  }, [pendingIndicatorText]);
  const ellipsisText = useMemo(() => {
    if (!LOADING_ELLIPSIS_SEQUENCE.length) {
      return '';
    }
    const safeIndex = ellipsisIndex % LOADING_ELLIPSIS_SEQUENCE.length;
    return LOADING_ELLIPSIS_SEQUENCE[safeIndex] || LOADING_ELLIPSIS_SEQUENCE[0];
  }, [ellipsisIndex]);
  const finalPendingText = useMemo(() => {
    if (!pendingIndicatorBaseText && !ellipsisText) {
      return '';
    }
    if (!pendingIndicatorBaseText) {
      return ellipsisText;
    }
    return `${pendingIndicatorBaseText}${ellipsisText}`;
  }, [ellipsisText, pendingIndicatorBaseText]);

  useEffect(() => {
    if (typeof onContainerRef === 'function') {
      const element = scrollContainerRef.current;
      console.debug('[ChatMessageList] register container', {
        element,
        isScrollable,
      });
      onContainerRef(element);
      return () => {
        console.debug('[ChatMessageList] unregister container', {
          element,
        });
        onContainerRef(null);
      };
    }
    return undefined;
  }, [onContainerRef]);


  useEffect(() => {
    if (!isScrollable) {
      return;
    }
    if (isTyping && scrollContainerRef?.current) {
      const container = scrollContainerRef.current;
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [isScrollable, isTyping, messages]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('[data-dropdown]');
      let clickedInside = false;
      
      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!hasPendingAssistantMessage) {
      setPendingMessageIndex(0);
      setEllipsisIndex(0);
      return undefined;
    }

    setPendingMessageIndex(0);
    setEllipsisIndex(0);

    if (typeof window === 'undefined') {
      return undefined;
    }

    let messageIntervalId = null;
    if (LOADING_MESSAGES.length > 1) {
      messageIntervalId = window.setInterval(() => {
        setPendingMessageIndex((prevIndex) => (prevIndex + 1) % LOADING_MESSAGES.length);
      }, LOADING_MESSAGE_INTERVAL_MS);
    }

    let ellipsisIntervalId = null;
    if (LOADING_ELLIPSIS_SEQUENCE.length > 0) {
      ellipsisIntervalId = window.setInterval(() => {
        setEllipsisIndex((prevIndex) => (prevIndex + 1) % LOADING_ELLIPSIS_SEQUENCE.length);
      }, LOADING_ELLIPSIS_INTERVAL_MS);
    }

    return () => {
      if (messageIntervalId) {
        window.clearInterval(messageIntervalId);
      }
      if (ellipsisIntervalId) {
        window.clearInterval(ellipsisIntervalId);
      }
    };
  }, [hasPendingAssistantMessage]);

  const hasExternalRetryingState = Boolean(retryingMessageMap);
  const activeSpinningMap = hasExternalRetryingState ? retryingMessageMap : internalSpinningMap;

  const handleRetryClick = (messageId, message) => {
    if (!messageId) {
      return;
    }
    if (!hasExternalRetryingState) {
      setInternalSpinningMap((prev) => ({ ...prev, [messageId]: true }));
    }
    try {
      onRetry?.(message);
    } finally {
      if (!hasExternalRetryingState) {
        window.setTimeout(() => {
          setInternalSpinningMap((prev) => ({ ...prev, [messageId]: false }));
        }, 900);
      }
    }
  };

  const handleCopyClick = (messageId, message) => {
    if (!messageId) {
      return;
    }
    onCopy?.(message);
    setInternalCopiedMap((prev) => ({ ...prev, [messageId]: true }));
    window.setTimeout(() => {
      setInternalCopiedMap((prev) => ({ ...prev, [messageId]: false }));
    }, 1600);
  };

  const toggleDropdown = (messageId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const closeDropdown = (messageId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [messageId]: false
    }));
  };

  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        'flex w-full flex-col gap-6',
        isScrollable && 'glass-scrollbar h-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
        className,
      )}
      aria-live="polite"
      role="log"
      data-testid="chat-message-list"
      data-assistant-name={title}
      style={{ 
        userSelect: 'text', 
        WebkitUserSelect: 'text',
        MozUserSelect: 'text',
        msUserSelect: 'text',
        position: 'relative', 
        zIndex: 0, 
        WebkitAppRegion: 'no-drag' 
      }}
    >
      {messages.map((message, index) => {
        const key = getMessageKey(message, `message-${index}`);
        const status = message?.status || 'complete';
        const role = message?.role || 'assistant';

        if (role === SYSTEM_ROLE) {
          return (
            <div key={key} className="flex justify-center" data-role="system" data-status={status}>
              <div
                className="max-w-[480px] rounded-xl border px-4 py-2 text-xs font-medium backdrop-blur-sm"
                style={{
                  backgroundColor: isLightTheme
                    ? 'rgba(148, 163, 184, 0.16)'
                    : 'rgba(30, 31, 45, 0.65)',
                  borderColor: resolvedPanelStyles.borderColor,
                  color: resolvedPanelStyles.subtleTextColor,
                }}
              >
                {message.text}
              </div>
            </div>
          );
        }

        if (role === USER_ROLE) {
          const hasText = Boolean(message?.text);
          const attachmentsNode = renderAttachments(
            message.attachments,
            resolvedTheme,
            resolvedPanelStyles,
          );

          return (
            <div key={key} className="flex justify-end" data-role="user" data-status={status}>
              <div
                className="break-words rounded-2xl border px-4 py-3 text-sm shadow-lg backdrop-blur-sm"
                style={{
                  maxWidth: userBubbleMaxWidth,
                  backgroundColor: isLightTheme
                    ? 'rgba(0, 0, 0, 0.05)'
                    : 'rgba(255, 255, 255, 0.08)',
                  borderColor: resolvedPanelStyles.borderColor,
                  color: resolvedPanelStyles.textColor,
                }}
              >
                {hasText ? (
                  <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
                ) : null}
                {attachmentsNode}
              </div>
            </div>
          );
        }

        const showActions = Boolean((onRetry || onCopy) && role !== SYSTEM_ROLE);
        const attachmentsNode = renderAttachments(
          message.attachments,
          resolvedTheme,
          resolvedPanelStyles,
        );
        const reasoningNode = renderReasoning(message.reasoning, status);
        const shouldShowPendingIndicator =
          status === 'pending' || (status === 'typing' && !message.text);

        return (
          <div key={key} className={`flex justify-center ${isFullscreen ? 'px-12' : 'px-4'}`} data-role="assistant" data-status={status}>
            <div 
              className="w-full min-w-0"
              style={{ 
                maxWidth: assistantMessageMaxWidth,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                boxSizing: 'border-box'
              }}
            >
              {shouldShowPendingIndicator && finalPendingText ? (
                <div
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: resolvedPanelStyles.subtleTextColor }}
                  role="status"
                  aria-live="polite"
                  data-testid="assistant-loading-indicator"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{finalPendingText}</span>
                </div>
              ) : null}
              {message.text ? (
                <div className="w-full">
                  <Response
                    className="w-full text-base leading-7"
                    style={{ color: resolvedPanelStyles.textColor }}
                  >
                    {message.text}
                  </Response>
                  {showActions && (
                    <Actions className="mt-1 relative z-10">
                  {(onRetry || onRetryWithModel) && (
                    <div className="relative" data-dropdown>
                      <Action
                        tooltip="다른 모델로 재생성"
                        label="Retry"
                        className="relative"
                        onClick={() => toggleDropdown(key)}
                      >
                        <RefreshCcwIcon className={`h-4 w-4 ${activeSpinningMap?.[key] ? 'animate-spin' : ''}`} />
                        <ChevronDown className="h-3 w-3 ml-0.5" />
                      </Action>
                      {availableModels.length > 0 && openDropdowns[key] && (
                        <div className="absolute top-8 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[99999] min-w-[160px]">
                          {availableModels.map((model) => (
                            <button
                              key={model.id}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => {
                                onRetryWithModel?.(message, model.id);
                                closeDropdown(key);
                              }}
                              disabled={activeSpinningMap?.[key]}
                            >
                              {model.label}
                            </button>
                          ))}
                          <button
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700 rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => {
                              handleRetryClick(key, message);
                              closeDropdown(key);
                            }}
                            disabled={activeSpinningMap?.[key]}
                          >
                            현재 모델로 재생성
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {onCopy && (
                    <Action
                      tooltip="클립보드에 복사"
                      label="Copy"
                      onClick={() => handleCopyClick(key, message)}
                    >
                      {internalCopiedMap[key] ? (
                        <svg
                          className="h-4 w-4 text-emerald-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        <CopyIcon className="h-4 w-4" />
                      )}
                    </Action>
                  )}
                </Actions>
                  )}
                </div>
              ) : null}
              {reasoningNode}
              {attachmentsNode}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
